import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { Unsubscriber } from '@core-lib/classes';
import { AppConstants } from '@core-lib/config';
import { IPays } from '@core-lib/models';
import { IAdresse, IAdresseForm } from '@core-lib/models/shared.models';
import { AdresseService } from '@core-lib/services/adresse.service';
import { SharedStoreSelectors } from '@core-lib/store/shared-store';
import { Store } from '@ngrx/store';
import { Observable, Subject, debounceTime, map, switchMap, takeUntil } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

@Component({
    selector: 'adresse',
    templateUrl: './adresse.component.html',
    styles: [],
    standalone: false
})
export class AdresseComponent extends Unsubscriber implements OnInit, OnChanges {
	@Input()
	set form(value: FormGroup<IAdresseForm>) {
		if (value) {
			this._form = value;
		} else {
			this.initData();
		}
	}
	private _form: FormGroup<IAdresseForm>;
	get form(): FormGroup<IAdresseForm> {
		return this._form;
	}
	@Input() isPersonneMorale: boolean = false;
	@Input() paysPersonneMoraleId: string;
	@Input() currentAdresse: IAdresse;
	@Input() isToConsult: boolean = true;

	@Output() changeAdresse: EventEmitter<IAdresse> = new EventEmitter();

	pays$ = this.store$.select(SharedStoreSelectors.selectPays);
	defaultPay: IPays = null;
	apiAdresses = [];
	searchTerm = new Subject<string | null>();
	isAdresseSelected: boolean = true;

	get paysResidenceIdField(): AbstractControl {
		return this.form?.get('paysResidenceId');
	}
	get adresseField(): AbstractControl {
		return this.form?.get('adresse');
	}
	get codePostalField(): AbstractControl {
		return this.form?.get('codePostal');
	}
	get villeField(): AbstractControl {
		return this.form?.get('ville');
	}
	get adressePostaleField(): AbstractControl {
		return this.form?.get('adressePostale');
	}

	get idField(): AbstractControl {
		return this.form?.get('id');
	}
	
	constructor(
		private store$: Store<any>,
		private adresseService: AdresseService
	) {
		super();
		this.searchTerm
			.pipe(
				debounceTime(300), // Attendre 300 ms entre chaque saisie
				distinctUntilChanged(), // Émettre une valeur uniquement si elle est différente de la précédente
				takeUntil(this.destroySubscriptions$),
				switchMap(term => this.loadItems(term))
			)
			.subscribe();
	}

	loadItems(term: string): Observable<any[]> {
		return this.getResult(term);
	}

	getResult(searchTerm: string): Observable<any[]> {
		if (!searchTerm) {
			return new Observable(observer => {
				this.apiAdresses = [];
				observer.next([]);
				observer.complete();
			});
		}

		return new Observable(() => {
			this.adresseService
				.searchGouv(searchTerm)
				.pipe(takeUntil(this.destroySubscriptions$))
				.subscribe(res => {
					this.apiAdresses = [];
					res.features.forEach(element => {
						this.apiAdresses.push(element.properties);
					});
					if (this.adressePostaleField?.value && this.paysResidenceIdField?.value == this.defaultPay?.id) {
						this.enableAdressFields();
						this.isAdresseSelected = false;
						this.adresseField.patchValue(null);
						this.codePostalField.patchValue(null);
						this.villeField.patchValue(null);
					} else {
						this.disableAdressFields();
					}
				});
		});
	}

	enableAdressFields(): void {
		this.adresseField?.enable();
		this.codePostalField?.enable();
		this.villeField?.enable();
	}

	disableAdressFields(): void {
		this.adresseField?.disable();
		this.codePostalField?.disable();
		this.villeField?.disable();
	}

	ngOnInit(): void {
		this.initData();
		this.setValidators();

		if(this.currentAdresse){
			this.adresseField?.patchValue(this.currentAdresse.adresse);
			this.codePostalField?.patchValue(this.currentAdresse.codePostal);
			this.villeField?.patchValue(this.currentAdresse.ville);
			this.paysResidenceIdField?.patchValue(this.currentAdresse.paysResidenceId);
			this.idField?.patchValue(this.currentAdresse.id);
			if(this.isToConsult){
				this.paysResidenceIdField?.disable();
				this.disableAdressFields();
			}
			else{
				this.paysResidenceIdField?.enable();
			}
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes && changes.paysPersonneMoraleId) {
			this.setValidatorsPersonMoral();
		}
	}

	buildForm(): void {
		this.form = new FormGroup<IAdresseForm>({
			adresse: new FormControl<string>({ value: null, disabled: true }),
			codePostal: new FormControl<string>({ value: null, disabled: true }),
			ville: new FormControl<string>({ value: null, disabled: true }),
			adressePostale: new FormControl<string>(null),
			id: new FormControl<string>(null),
		});

		if (!this.isPersonneMorale || !this.form.contains('paysResidenceId')) {
			this.form.addControl('paysResidenceId', new FormControl<string | null>(this.defaultPay?.id, [Validators.required]));
		} else if (this.isPersonneMorale) {
			this.setValidatorsPersonMoral();
			if (this.form.contains('paysResidenceId')) {
				this.form.removeControl('paysResidenceId');
			}
		}
	}

	initData(): void {
		this.pays$.pipe(takeUntil(this.destroySubscriptions$)).subscribe(res => {
			this.defaultPay = res?.find(x => x.codeISO === AppConstants.FRANCE_CODE_ISO);
			if (!this.form) {
				this.buildForm();
			} else {
				//ajouter les validateurs en initialisation
				if (this.form?.value?.paysResidenceId != this.defaultPay?.id) {
					this.enableAdressFields();
				} else {
					this.disableAdressFields();
				}
			}
			this.changeAdresse.emit(this.form.getRawValue() as IAdresse);
		});
	}

	setValidators(): void {
		this.paysResidenceIdField?.valueChanges
			.pipe(
				map(_ => this.paysResidenceIdField.value),
				takeUntil(this.destroySubscriptions$)
			)
			.subscribe(value => {
				if (value != this.defaultPay?.id) {
					this.enableAdressFields();
				} else {
					this.disableAdressFields();
				}
				this.changeAdresse.emit(this.form.getRawValue() as IAdresse);
			});
	}

	setValidatorsPersonMoral(): void {
		this.defaultPay?.id === this.paysPersonneMoraleId ? this.disableAdressFields() : this.enableAdressFields();
	}

	onChange($event): void {
		this.isAdresseSelected = true;
		this.adressePostaleField?.patchValue($event ? $event.label : null);
		if (this.adressePostaleField?.value) {
			this.enableAdressFields();
			const parts = $event?.label.split($event?.postcode);
			this.adresseField.patchValue(parts[0]);
			this.codePostalField.patchValue($event?.postcode);
			this.villeField.patchValue($event?.city);
		} else {
			this.disableAdressFields();
			this.adresseField.patchValue(null);
			this.codePostalField.patchValue(null);
			this.villeField.patchValue(null);
		}
		this.changeAdresse.emit(this.form.getRawValue() as IAdresse);
	}

	onInput($event): void {
		this.adressePostaleField?.patchValue($event.target.value);
	}

	onBlur(): void {
		this.changeAdresse.emit(this.form.getRawValue() as IAdresse);
	}
}
